import { useKeycloak } from "@react-keycloak/web";
import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { silentReauth } from "../../utils/silentReauth";
import { getJwtLanguage } from "@dreebit-registry/vsm-microservice-components";
import i18n from "../../i18n/config";
import { getLastTokenResponseForSentry } from "../tokenEndpointMonitor";

const MAX_REFRESH_FAILURES = 3;

type RefreshTrigger = 'interval' | 'visibility';

const getTokenDiagnostics = (keycloak: ReturnType<typeof useKeycloak>['keycloak']) => {
    const tokenParsed = keycloak.tokenParsed as { exp?: number; iat?: number } | undefined;
    const refreshTokenParsed = keycloak.refreshTokenParsed as { exp?: number; typ?: string } | undefined;
    const now = Math.floor(Date.now() / 1000);
    return {
        userId: keycloak.subject,
        authenticated: keycloak.authenticated,
        tokenExpiresInSeconds: tokenParsed?.exp ? tokenParsed.exp - now : null,
        refreshTokenExpiresInSeconds: refreshTokenParsed?.exp ? refreshTokenParsed.exp - now : null,
        refreshTokenType: refreshTokenParsed?.typ,
        tabVisibility: document.visibilityState,
    };
};

const KeycloakSessionHandler = () => {
    const { keycloak } = useKeycloak();
    const refreshFailCount = useRef(0);

    const syncLanguage = () => {
        const newLanguage = getJwtLanguage(keycloak.token);
        const currentLanguage = sessionStorage.getItem('lng');

        if (newLanguage && newLanguage !== currentLanguage) {
            console.log(`[i18n] Language changed from ${currentLanguage} to ${newLanguage}`);
            sessionStorage.setItem('lng', newLanguage);
            i18n.changeLanguage(newLanguage);
        }
    };

    const refreshToken = (trigger: RefreshTrigger) => {
        if (!keycloak?.authenticated) return;

        const diagnostics = getTokenDiagnostics(keycloak);

        keycloak.updateToken(70)
            .then((refreshed) => {
                refreshFailCount.current = 0;
                Sentry.addBreadcrumb({
                    category: 'keycloak',
                    level: 'info',
                    message: refreshed ? 'Token refreshed' : 'Token still valid (no refresh needed)',
                    data: { trigger, ...diagnostics },
                });
                if (refreshed) {
                    console.log("[Keycloak] Token automatisch erneuert");
                    syncLanguage();
                }
            })
            .catch((err) => {
                refreshFailCount.current++;
                const willReauth = refreshFailCount.current >= MAX_REFRESH_FAILURES;
                const tokenResponse = getLastTokenResponseForSentry();
                const serverError = tokenResponse.lastTokenResponse?.error;
                const serverErrorDescription =
                    tokenResponse.lastTokenResponse?.errorDescription;
                console.warn(
                    `[Keycloak] Token refresh failed (${refreshFailCount.current}/${MAX_REFRESH_FAILURES}): ${serverError ?? 'unknown'}` +
                        (serverErrorDescription ? ` — ${serverErrorDescription}` : ''),
                    err,
                );

                Sentry.captureException(
                    err instanceof Error ? err : new Error('Keycloak updateToken failed'),
                    {
                        level: willReauth ? 'error' : 'warning',
                        tags: {
                            keycloak: 'refresh_failed',
                            willReauth: String(willReauth),
                            trigger,
                            tokenEndpointStatus: String(
                                tokenResponse.lastTokenResponse?.status ?? 'unknown',
                            ),
                            tokenEndpointError: String(serverError ?? 'unknown'),
                        },
                        extra: {
                            failureCount: refreshFailCount.current,
                            maxFailures: MAX_REFRESH_FAILURES,
                            errorValue: err,
                            ...diagnostics,
                            ...tokenResponse,
                        },
                    },
                );

                if (willReauth) {
                    silentReauth(keycloak, `refresh_failed_${MAX_REFRESH_FAILURES}x`);
                }
            });
    };

    useEffect(() => {
        const interval = setInterval(() => refreshToken('interval'), 60_000);

        // Refresh token immediately when the user returns to the tab
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshToken('visibility');
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keycloak]);

    return null;
};

export default KeycloakSessionHandler;
